/**
 * Staff roles and what each one is allowed to do.
 *
 * The database enforces the same split through RLS (see the `is_staff` and
 * `can_manage_bookings` helpers), so this file is the shared vocabulary the
 * pages use to hide what a person cannot do anyway.
 */

export const STAFF_ROLES = ["admin", "manager", "staff", "crew"] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

export const ROLE_LABELS: Record<StaffRole, string> = {
  admin: "Admin",
  manager: "Manager",
  staff: "Staff",
  crew: "Dock & Processing",
};

export const ROLE_SUMMARY: Record<StaffRole, string> = {
  admin: "Full access, including money and who gets which role.",
  manager: "Approves requests, assigns rooms and boats, bills stays, sees money.",
  staff: "Sees the calendar, stays and staffing, but changes nothing.",
  crew: "Sees the day-to-day calendar and the staffing grid only.",
};

export type Capability =
  | "viewCalendar"
  | "viewStaffing"
  | "manageStaffing"
  | "viewBookings"
  | "manageBookings"
  | "reviewSubmissions"
  | "viewMoney"
  | "manageRoles";

/** Which roles hold each capability. Order matters only for display. */
export const CAPABILITIES: { key: Capability; label: string; roles: StaffRole[] }[] = [
  {
    key: "viewCalendar",
    label: "See the booking calendar",
    roles: ["admin", "manager", "staff", "crew"],
  },
  {
    key: "viewStaffing",
    label: "See who is working which day",
    roles: ["admin", "manager", "staff", "crew"],
  },
  { key: "manageStaffing", label: "Change the staffing grid", roles: ["admin", "manager"] },
  { key: "viewBookings", label: "See booking requests and stays", roles: ["admin", "manager", "staff"] },
  {
    key: "manageBookings",
    label: "Approve requests, assign rooms and boats, add charges",
    roles: ["admin", "manager"],
  },
  { key: "reviewSubmissions", label: "Approve guest photos and reviews", roles: ["admin", "manager"] },
  { key: "viewMoney", label: "See payouts, revenue and billing", roles: ["admin", "manager"] },
  { key: "manageRoles", label: "Decide who gets which role", roles: ["admin"] },
];

const CAPABILITY_MAP = new Map<Capability, StaffRole[]>(
  CAPABILITIES.map((c) => [c.key, c.roles]),
);

export function can(role: StaffRole | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  return (CAPABILITY_MAP.get(capability) ?? []).includes(role);
}

/** Picks the strongest role when someone holds more than one. */
export function highestRole(roles: readonly string[]): StaffRole | null {
  for (const role of STAFF_ROLES) {
    if (roles.includes(role)) return role;
  }
  return null;
}

export function isStaffRole(value: unknown): value is StaffRole {
  return typeof value === "string" && (STAFF_ROLES as readonly string[]).includes(value);
}
